import React, { createContext, useState, useEffect, useContext } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

// Create context
const PublicThemeContext = createContext();

// Custom hook to use the public theme context
export const usePublicTheme = () => {
  return useContext(PublicThemeContext);
};

// Font loading utility
const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'Roboto') return; // Roboto is already loaded by Material-UI

  const fontId = `font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;

  // Check if font is already loaded
  if (document.getElementById(fontId)) return;

  const link = document.createElement('link');
  link.id = fontId;
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

export const PublicThemeProvider = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  const secondaryColor = '#dc004e'; // Fixed secondary color
  const fontFamily = 'Roboto'; // Fixed font family
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPhotoCrop, setCoverPhotoCrop] = useState({
    x: 0,
    y: 0,
    width: 1,
    height: 1
  });
  const [coverName, setCoverName] = useState('');
  const [coverDescription, setCoverDescription] = useState('');
  const [theme, setTheme] = useState(null);
  const [themeReady, setThemeReady] = useState(true);

  // Create theme based on colors and font
  useEffect(() => {
    const newTheme = createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: secondaryColor,
        },
        background: {
          default: '#f5f5f5',
          paper: '#ffffff',
        },
      },
      typography: {
        fontFamily: [
          fontFamily,
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
        ].join(','),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    });

    setTheme(newTheme);
  }, [primaryColor, secondaryColor, fontFamily]);

  // Load font when fontFamily changes
  useEffect(() => {
    loadGoogleFont(fontFamily);
  }, [fontFamily]);

  // Reset theme to defaults
  const resetTheme = () => {
    setPrimaryColor('#1976d2');
    setCoverPhoto(null);
    setCoverPhotoCrop({
      x: 0,
      y: 0,
      width: 1,
      height: 1
    });
    setCoverName('');
    setCoverDescription('');
  };

  // Update theme when merchant changes
  const updateMerchantTheme = (merchantData) => {
    console.log('🎨 PublicTheme: Updating merchant theme with data:', merchantData);

    if (merchantData) {
      const primaryColor = merchantData.primary_color || '#1976d2';
      const coverPhoto = merchantData.cover_photo || null;
      const coverName = merchantData.cover_name || '';
      const coverDescription = merchantData.cover_description || '';

      // Handle crop coordinates
      const cropData = {
        x: merchantData.cover_photo_crop_x !== undefined ? merchantData.cover_photo_crop_x : 0,
        y: merchantData.cover_photo_crop_y !== undefined ? merchantData.cover_photo_crop_y : 0,
        width: merchantData.cover_photo_crop_width !== undefined ? merchantData.cover_photo_crop_width : 1,
        height: merchantData.cover_photo_crop_height !== undefined ? merchantData.cover_photo_crop_height : 1
      };

      console.log('🎨 PublicTheme: Raw crop values from backend:', {
        cover_photo_crop_x: merchantData.cover_photo_crop_x,
        cover_photo_crop_y: merchantData.cover_photo_crop_y,
        cover_photo_crop_width: merchantData.cover_photo_crop_width,
        cover_photo_crop_height: merchantData.cover_photo_crop_height
      });

      console.log('🎨 PublicTheme: Applying theming:', {
        primaryColor,
        coverPhoto,
        coverName,
        coverDescription,
        cropData
      });

      // Set primary color
      setPrimaryColor(primaryColor);

      // Set cover photo
      setCoverPhoto(coverPhoto);

      // Set cover photo crop coordinates
      setCoverPhotoCrop(cropData);

      // Set cover name
      setCoverName(coverName);

      // Set cover description
      setCoverDescription(coverDescription);

      // Mark theme as ready
      setThemeReady(true);
      console.log('🎨 PublicTheme: Theme marked as ready');
    } else {
      console.log('🎨 PublicTheme: No merchant data provided, using defaults');
      // If no merchant data, reset to defaults and mark as ready
      resetTheme();
      setThemeReady(true);
    }
  };

  const value = {
    theme,
    themeReady,
    primaryColor,
    secondaryColor,
    fontFamily,
    coverPhoto,
    coverPhotoCrop,
    coverName,
    coverDescription,
    updateMerchantTheme,
    loadGoogleFont
  };

  return (
    <PublicThemeContext.Provider value={value}>
      {theme ? (
        <MuiThemeProvider theme={theme}>
          {children}
        </MuiThemeProvider>
      ) : (
        children
      )}
    </PublicThemeContext.Provider>
  );
};
